/** PROTOTYPE data-source editor -- editable list of sources (mill / vendor / contractor). */
import React from 'react';
import { CRC } from './moduleInputsData';
import { Label, NumberInput, Toggle } from './protoAtoms';
import { ModuleSource, SourceFormat, SOURCE_FORMATS, SOURCE_FORMAT_LABELS, sourceCount } from './spreadSourceData';

const textInput: React.CSSProperties = { width: 120, padding: '8px 10px', fontSize: 13, borderRadius: 8, border: `1.5px solid ${CRC.border}`, color: CRC.blueprintBlue, fontFamily: 'inherit' };
const selectInput: React.CSSProperties = { ...textInput, width: 140 };
const linkBtn: React.CSSProperties = { fontSize: 11, color: CRC.readableRed, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' };

interface Props {
  sources: ModuleSource[];
  setSources: (sources: ModuleSource[]) => void;
  noun: string;        // "Mill" / "Vendor" / "Contractor"
  countLabel: string;  // "Pipe count" / "Inspections" / "Joints"
  joints: number;      // the spread's joint count, for the shortcut
}

export const SpreadSourceInputs: React.FC<Props> = ({ sources, setSources, noun, countLabel, joints }) => {
  const patch = (id: string, p: Partial<ModuleSource>) => setSources(sources.map((s) => (s.id === id ? { ...s, ...p } : s)));
  const add = () => {
    const n = sources.length + 1;
    setSources([...sources, { id: `${noun.toLowerCase()}-${n}-${sources.length}`, name: `${noun} ${n}`, format: 'spreadsheet', pipeCount: joints, chargeNre: true }]);
  };
  const remove = (id: string) => setSources(sources.filter((s) => s.id !== id));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue, marginBottom: 14 }}>Data sources ({noun.toLowerCase()}s)</div>
        <button style={{ ...linkBtn, fontSize: 12.5 }} onClick={add}>+ Add {noun.toLowerCase()}</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sources.map((s) => (
          <div key={s.id} style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', paddingBottom: 12, borderBottom: `1px solid ${CRC.rowDivider}` }}>
            <div><Label>Name</Label><div style={{ marginTop: 4 }}>
              <input title={`${noun} name`} value={s.name} onChange={(e) => patch(s.id, { name: e.target.value })} style={textInput} />
            </div></div>
            <div><Label>Data format</Label><div style={{ marginTop: 4 }}>
              <select title="Data format" value={s.format} onChange={(e) => patch(s.id, { format: e.target.value as SourceFormat })} style={selectInput}>
                {SOURCE_FORMATS.map((f) => <option key={f} value={f}>{SOURCE_FORMAT_LABELS[f]}</option>)}
              </select>
            </div></div>
            <div><Label>{countLabel}</Label><div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <NumberInput value={s.pipeCount} onChange={(v) => patch(s.id, { pipeCount: Math.max(0, v) })} width={110} />
              {s.pipeCount !== joints && (
                <button style={{ ...linkBtn, textDecoration: 'underline' }} title={`Set to the spread's joint count`}
                  onClick={() => patch(s.id, { pipeCount: joints })}>use {joints.toLocaleString()} joints</button>
              )}
            </div></div>
            <div><Label>Onboarding (NRE)</Label><div style={{ marginTop: 8 }}>
              <Toggle on={s.chargeNre} onClick={() => patch(s.id, { chargeNre: !s.chargeNre })} label={s.chargeNre ? 'Charged' : 'Skipped (already onboarded)'} />
            </div></div>
            <button style={{ ...linkBtn, color: CRC.heritageGrey }} onClick={() => remove(s.id)} disabled={sources.length === 1} title={sources.length === 1 ? `Keep at least one ${noun.toLowerCase()}` : `Remove ${noun.toLowerCase()}`}>remove</button>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, fontSize: 11.5, color: CRC.heritageGrey }}>
        Total records: <strong style={{ color: CRC.blueprintBlue }}>{sourceCount(sources).toLocaleString()}</strong> · this spread has {joints.toLocaleString()} joints. Each {noun.toLowerCase()} onboards once (NRE); untick it for one already onboarded on a past project.
      </div>
    </div>
  );
};
